import router from '@/router';
import toast from '@/modules/toast/services';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import { modifiedProperties } from '@/utils';
import pick from 'lodash/pick';
import { formatErrorResponseMessage } from '@/api/interceptors/format-error';
import api from '../api';

export default {
  /**
   * Initialize the content editor from the api.
   * @return {Promise}
   */
  async INIT_EDITOR({ commit, dispatch }, {
    id, relativeVersionId, type, channel, language,
  }) {
    const content = await api.getContent({
      id, relativeVersionId, type, language,
    });
    commit('INIT_EDITOR', content);

    const channels = Object.keys(content.channels);
    let currentChannel = channel;

    // Multilang channels
    if (language) {
      const translationChannel = `translation:${language}`;
      if (channels.includes(translationChannel)) {
        currentChannel = translationChannel;
      }
    }

    // Set current channel
    if (channels.includes(currentChannel)) {
      commit('SET_CURRENT_CHANNEL', currentChannel);
    } else {
      // Redirect to master if the channel is not present, replacing channel
      dispatch('CHANGE_CHANNEL', { channel: 'master', replace: true });
    }

    return content;
  },

  /**
   * Add a new channel to the editor
   * @param String channel
   * @return {Promise}
   */
  async ADD_CHANNEL({ dispatch, state, commit }, channel) {
    // Navigate to channel route
    try {
      const fields = await api.addChannel(
        state.id,
        state.relativeVersionId,
        channel,
        { language: state.currentLanguage },
      );
      commit('ADD_CHANNEL', { channel, fields });
      dispatch('CHANGE_CHANNEL', { channel });
    } catch (error) {
      console.error(error);
    }
  },

  /**
   * Remove a channel from the editor
   * @param String channel
   * @return {Promise}
   * @param channel
   */
  async REMOVE_CHANNEL({ dispatch, state, commit }, channel) {
    try {
      await api.removeChannel(state.id, state.relativeVersionId, channel, { language: state.currentLanguage });
      await dispatch('CHANGE_CHANNEL', { channel: 'master' });
      commit('REMOVE_CHANNEL', channel);
      toast.notify('message.content.editor.deleteChannelSuccess');
    } catch (error) {
      console.error(error);
    }
  },

  /**
   * Change the current editor channel
   * @param channel String channel
   * @param replace Boolean Force route replace instead push
   * @return {Promise}
   */
  async CHANGE_CHANNEL(
    {
      commit, dispatch, state, getters,
    },
    {
      channel, replace = false,
    },
  ) {
    const { id, relativeVersionId, type } = state;
    if (!id || !type) {
      console.error('Cannot change to channel', channel);
      return false;
    }
    if (getters.modifiedChannels.includes(channel)) {
      // Not loaded channel
      if (channel in state.original.channels === false) {
        return dispatch('ADD_CHANNEL', channel);
      }
      commit('SET_CURRENT_CHANNEL', channel);
      // TODO: Este método puede que nos de problemas en los editores modales.
      // Si es así, refactorizar para emitir evento con el nuevo valor
      // @see updateRelativeVersionId events
      return dispatch('UPDATE_ROUTE', {
        id, relativeVersionId, type, channel, replace,
      });
    }
    return false;
  },

  /**
   * Updates the given fields in the current channel draft.
   * @return {Promise}
   */
  async UPDATE_ROUTE(context, {
    id, relativeVersionId, type, channel, replace = false,
  }) {
    const routeParams = {
      name: 'content.editor',
      params: {
        id, relativeVersionId, type, channel,
      },
    };
    if (replace) {
      router.replace(routeParams);
    } else {
      router.push(routeParams);
    }
  },

  /**
   * Updates the given fields in the current channel draft.
   * @return {Promise}
   */
  async UPDATE_FIELD({ commit, state, getters }, fields) {
    // If some of the fields are binded to master, unbind it
    const channel = state.currentChannel;
    if (channel !== 'master') {
      Object.keys(fields).forEach((field) => {
        if (getters.bindFields.includes(field)) {
          fields.bindFields = getters.bindFields.filter(bindField => bindField !== field);
        }
      });
    }
    commit('UPDATE_CHANNEL_DRAFT', { channel, fields });
  },

  /**
   * Update a channel vee-validate form flags
   * @return {Promise}
   */
  async UPDATE_CHANNEL_FORM_FLAGS({ commit, getters }, flags) {
    const channel = getters.channel;
    if (isEqual(channel.flags, flags)) return;
    commit('SET_CHANNEL_FORM_FLAGS', { channel: channel.name, flags });
  },

  /**
   * Update a channel vee-validate form errors
   * @return {Promise}
   */
  async UPDATE_CHANNEL_FORM_ERRORS({ commit, getters }, errors) {
    const channel = getters.channel;
    if (isEqual(channel.errors, errors)) return;
    commit('SET_CHANNEL_FORM_ERRORS', { channel: channel.name, errors });
  },

  /**
   * Update a channel vee-validate form fields
   * @return {Promise}
   */
  async UPDATE_CHANNEL_FORM_FIELDS({ commit, getters }, fields) {
    const channel = getters.channel;
    if (isEqual(channel.fields, fields)) return;
    commit('SET_CHANNEL_FORM_FIELDS', { channel: channel.name, fields });
  },

  /**
   * Saves the content.
   *
   * @param commit
   * @param state
   * @param {String} id The content Id.
   * @returns {Promise}
   */
  async SAVE({ commit, state }) {
    const diff = {};
    const { id, type, relativeVersionId } = state;
    // Iterate over channels finding modified fields
    Object.entries(state.channels).forEach(([channel, { draft }]) => {
      const modifiedFields = modifiedProperties(draft, state.original.channels[channel]);
      const channelDiff = pick(draft, modifiedFields);
      if (isEmpty(channelDiff)) return;
      // Find if there are fields to be rebind to master and delete from the payload
      if (Array.isArray(channelDiff.bindFields)) {
        if (channelDiff.bindFields.length === 0) {
          delete channelDiff.bindFields;
        } else {
          channelDiff.bindFields.forEach((bindField) => {
            delete channelDiff[bindField];
          });
        }
      }
      diff[channel] = channelDiff;
    });

    // Execute beforeSaveFunctionHooks if exists
    const context = {
      diff,
      abort: false,
    };
    for (let i = 0; i < state.beforeSaveCallbacks.length; i += 1) {
      const asyncFn = state.beforeSaveCallbacks[i];
      await asyncFn(context);
      if (context.abort) {
        break;
      }
    }
    if (context.abort) {
      console.log('Save action aborted in beforeSaveCallback');
      return false;
    }

    // Now we call the backend api to persist the diff
    try {
      const content = await api.updateContent(id, relativeVersionId, type, diff, { language: state.currentLanguage });
      commit('SET_CURRENT_CONTENT', content);
      toast.notify('message.content.tools.savedSuccess');
    } catch (error) {
      console.error(error);
      toast.error(formatErrorResponseMessage(error.response.data, 'message.content.tools.savedFailed'));
      return false;
    }
    return true;
  },

  /**
   * Publish the current content
   *
   * @param commit
   * @param getters
   * @param state
   * @param dispatch
   * @returns {Promise}
   */
  async PUBLISH({
    commit, getters, state, dispatch,
  }) {
    try {
      // Execute beforePublishFunctionHooks if exists
      const context = {
        abort: false,
      };
      for (let i = 0; i < state.beforePublishCallbacks.length; i += 1) {
        const asyncFn = state.beforePublishCallbacks[i];
        await asyncFn(context);
        if (context.abort) {
          break;
        }
      }
      if (context.abort) {
        console.log('Publish action aborted in beforePublishCallback');
        return {};
      }

      const {
        id, relativeVersionId, type, currentChannel, currentLanguage,
      } = state;

      const result = await api.publishContent(id, relativeVersionId, type, { language: state.currentLanguage });
      commit('PUBLISH_CONTENT', result);
      toast.notify('message.content.tools.publishSuccess');
      // Dispatch init editor when editor is not versionable.
      // On versionable entity init will be done watching relativeVersionId prop refs
      if (!getters.versionable) {
        await dispatch('INIT_EDITOR', {
          id,
          type,
          channel: currentChannel,
          language: currentLanguage,
        });
      }
      return result;
    } catch (error) {
      console.error(error);
      return {};
    }
  },

  /**
   * Unpublish the current content
   *
   * @param commit
   * @param state
   * @param dispatch
   * @param redirectCode
   * @param destination
   * @returns {Promise}
   */
  async UNPUBLISH({ commit, state, dispatch }, { redirectCode, destination } = {}) {
    try {
      const {
        id, type, currentLanguage,
      } = state;
      const payload = { redirectCode };
      if (redirectCode <= 400) {
        payload.destination = destination;
      }
      const result = await api.unpublishContent(
        id,
        type,
        payload,
        { language: currentLanguage },
      );
      toast.notify('message.content.tools.unpublishedSuccess');
      commit('PUBLISH_CONTENT', result);
      return result;
    } catch (error) {
      toast.notify('message.content.tools.unpublishedFail');
      console.error(error);
      throw error;
    }
  },

  /**
   * Clone the current content
   *
   * @param commit
   * @returns {Promise}
   */
  async CLONE({ state, rootGetters }) {
    toast.notify('message.content.tools.cloning');
    try {
      let result;
      if (state.relativeVersionId) {
        // @see https://mgr.bitban.com/issues/106814
        result = await api.cloneContentFromRelativeVersionId(state.id, state.type, state.relativeVersionId);
      } else {
        result = await api.cloneContent(state.id, state.type);
      }

      toast.notify('message.content.tools.clonedSuccess');
      // Open the content in new window
      const editorUrl = router.resolve({
        name: 'content.editor',
        params: {
          id: result.id,
          type: result.type,
          site: rootGetters.site.idAlias,
        },
      });
      window.open(editorUrl.href, '_blank');
    } catch (error) {
      toast.notify('message.content.tools.clonedFail');
      console.error(error);
    }
  },

  /**
   * Clone the current version
   *
   * @param commit
   * @returns {Promise}
   */
  async CLONE_VERSION({ state }) {
    toast.notify('message.content.tools.cloningVersion');
    try {
      const result = await api.cloneContentVersion(
        state.id,
        state.relativeVersionId,
        state.type,
        { language: state.currentLanguage },
      );
      toast.notify('message.content.tools.clonedVersionSuccess');
      return result;
    } catch (error) {
      toast.notify('message.content.tools.clonedVersionFail');
      console.error(error);
      return {};
    }
  },

  /**
   * Delete the current content
   *
   * @param commit
   * @returns {Promise}
   */
  async DELETE({ state }) {
    try {
      await api.deleteContent(state.id, state.type);
      toast.notify('message.content.tools.deletedSuccess');
      // Open the content in new window
      // Return to content list
      const type = state.type;
      router.push({ name: 'searcher', params: { entity: 'content' }, query: { type } });
    } catch (error) {
      toast.notify('message.content.tools.deletedFail');
      console.error(error);
    }
  },

  /**
   * Bind a channel field to master value
   *
   * @param commit
   * @param {String} field The field to bind.
   * @returns {Promise}
   */
  async BIND_CHANNEL_FIELD({ dispatch, getters }, field) {
    const bindFields = uniq([...getters.draft.bindFields || [], field]);
    const masterFieldValue = getters.master[field];
    dispatch('UPDATE_FIELD', { bindFields, [field]: masterFieldValue });
  },

  /**
   * Toggle the editor between edition and preview modes
   *
   * @param commit
   * @returns {Promise}
   */
  async TOGGLE_PREVIEW_MODE({ commit }) {
    commit('TOGGLE_PREVIEW_MODE');
  },

  /**
   * Change current preview url
   *
   * @param commit
   * @returns {Promise}
   */
  FORCE_PREVIEW_URL({ commit }, previewUrl) {
    commit('SET_SELECTED_PREVIEW_URL', previewUrl);
  },

  /**
   * @param state
   * @param commit
   * @param channel
   * @param versionId
   * @return {boolean}
   */
  async RESTORE_CHANNEL_VERSION({ state, commit }, { versionId }) {
    try {
      const editor = await api.restoreVersion(state.id, state.type, state.currentChannel, versionId);
      commit('INIT_EDITOR', editor);
      toast.notify('message.content.tools.actionsLog.restoreSuccess');
    } catch (error) {
      // TODO: Gestionar el error
      console.error(error);
      toast.error('message.content.tools.actionsLog.restoreFailed');
      return false;
    }
    return true;
  },

  TOGGLE_ADVANCED_EDITOR({ commit }) {
    commit('TOGGLE_ADVANCED_EDITOR');
  },

  ENABLE_ADVANCED_EDITOR({ commit }) {
    commit('ENABLE_ADVANCED_EDITOR');
  },

  ADD_BEFORE_SAVE_CALLBACK({ commit }, asyncFn) {
    commit('ADD_BEFORE_SAVE_CALLBACK', asyncFn);
  },

  ADD_BEFORE_PUBLISH_CALLBACK({ commit }, asyncFn) {
    commit('ADD_BEFORE_PUBLISH_CALLBACK', asyncFn);
  },

  MODIFY_AVAILABLE_LANGUAGES({ commit }, availableLanguages) {
    commit('MODIFY_AVAILABLE_LANGUAGES', availableLanguages);
  },
};
